import { useCallback, useMemo, useRef } from "react";
import { createIdempotencyKey } from "@src/packages/api/idempotency";
import {
  useCreatePatientNote,
  useDeletePatientNote,
  usePatientNotes,
  useUpdatePatientNote,
  type CreateNotePayload,
} from "@src/packages/care/hooks/usePatientNotes";
import {
  getPregnancyDetailsNote,
  serializePregnancyDetailsNote,
} from "./pregnancyDetailsNote";

/**
 * Her own words about a pregnancy that ended or a birth, read and saved as a note linked
 * to the cycle. See `pregnancyDetailsNote` for why they live there.
 *
 * `save` makes the note say exactly what the field says: it writes the words,
 * changes them, or, when she has cleared the field, removes the note. Saving
 * the same words twice writes nothing, so a retried sheet does not add a note.
 */
export function usePregnancyDetailsNote(cycleId: string | null | undefined) {
  const notes = usePatientNotes();
  const createNote = useCreatePatientNote();
  const updateNote = useUpdatePatientNote();
  const deleteNote = useDeletePatientNote();
  const saved = useMemo(
    () => (cycleId ? getPregnancyDetailsNote(notes.data, cycleId) : null),
    [cycleId, notes.data],
  );
  /**
   * A first save whose response was lost, kept so the retry replays it.
   * The same key and the same body, which is what the API needs to replay
   * the note rather than write a second one.
   */
  const pendingCreate = useRef<{
    details: string;
    key: string;
    payload: CreateNotePayload;
  } | null>(null);

  const save = useCallback(
    async (details: string, today: string) => {
      if (!cycleId) return;
      const text = details.trim();
      if (saved) {
        if (saved.record.details === text) return;
        if (!text) {
          await deleteNote.mutateAsync(saved.note.id);
          return;
        }
        await updateNote.mutateAsync({
          noteId: saved.note.id,
          text: serializePregnancyDetailsNote({
            cycleId,
            details: text,
            recordedAt: new Date().toISOString(),
          }),
        });
        return;
      }
      if (!text) return;
      const pending =
        pendingCreate.current?.details === text
          ? pendingCreate.current
          : {
              details: text,
              key: createIdempotencyKey(),
              payload: {
                cycleId,
                date: today,
                text: serializePregnancyDetailsNote({
                  cycleId,
                  details: text,
                  recordedAt: new Date().toISOString(),
                }),
              },
            };
      pendingCreate.current = pending;
      await createNote.mutateAsync({
        ...pending.payload,
        idempotencyKey: pending.key,
      });
      pendingCreate.current = null;
    },
    [createNote, cycleId, deleteNote, saved, updateNote],
  );

  return {
    /** What she last wrote for this cycle, or null when she has written nothing. */
    details: saved?.record.details ?? null,
    /** False while her notes are still on their way. */
    ready: !notes.isLoading,
    save,
  };
}
